import clipboardCopy from 'clipboard-copy';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import QRCode from 'qrcode.react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FaCopy, FaSpinner } from 'react-icons/fa';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import Logo from "../../../assets/images/LogoNew.svg";
import { ApplicantHeaderActions, createApplicationFormUpdateHandler } from '../../../components/ApplicationInvite';
import AutoSaveIndicator from '../../../components/AutoSaveIndicator/AutoSaveIndicator';
import CountrySelectService from '../../../components/CSC/CountrySelect/CountrySelectService';
import Stepper from '../../../components/Common/Tab/Stepper';
import { FORM_AUTOFILL_PROPS } from '../../../components/InputField/autofillProps';
import { buildAutomationChildTestId } from '../../../components/InputField/testId';
import LanguagePicker from '../../../components/LanguagePicker/LanguagePicker';
import LogoutWithReason from '../../../components/LogoutWithReason/LogoutWithReason';
import PayNow from '../../../components/Payment/PayNow';
import ReviewShellBoundary from '../../../components/ReviewWizard/ReviewShellBoundary';
import useAutoSave from '../../../hooks/useAutoSave';
import useFormReviewShell from '../../../hooks/useFormReviewShell';
import useUnsavedChangesWarning from '../../../hooks/useUnsavedChangesWarning';
import { DialogContext } from '../../../store/context/DialogContext';
import { useTranslation } from '../../../store/context/TranslationContext';
import { doGET, doPUT } from '../../../util/HttpUtil';
import { handleUtmFormSubmit } from '../../../util/utm';
import pincodeService from '../components/PincodeField/PincodeService';
import { withComputedHidePinCode } from '../components/PincodeField/helper';
import { createIndividualLiteAutoSaveAdapter } from './autosave/buildIndividualLiteAutoSavePayload';
import { buildPostAutoSaveUpdatePayload, createAutoSaveFieldDefinitionMap, getAutoSaveProceedBlockedMessage, isAutoSaveEnabledForConfig, resolveAutoSaveDebounceMs } from './autosave/shared';
import { StatusWithBadge } from '../thirdParty/ondato/Ondato';
import ExtraQuestions from './ExtraQuestions';
import { hasActiveAsks, hasPendingAsks } from './askIdentity';
import { checkIsAskForInfo, convertArrayToDict, fetchIndividualLiteData, FORM_TYPE, scrollToTop, StaticKYCContent, STEP_STATUS, STEPS } from './IPlushelper';
import { AskMoreMessage, DeclarationsForm, ExpectedAccountActivityForm, PreKYCInfo, ThankYou, UserDetailsForm } from './components';
import ApplicationDocRedo from './components/IndividualLite/ApplicationDocRedo';
import ProofOfAddress from './components/IndividualLite/ProofOfAddressDocument';
import { SUBMIT_DOC_FLOW, validateSubmitDocuments } from './submitDocumentValidation';
// Document-first (story 15920): reuse the Business V2 doc-first Documents shell + extraction
// gate for the individual GMA flow. Every usage below is guarded by isDocFirst, so the legacy
// individual flow is unchanged when doc-first is off (non-broker, flag off, or config not served).
import DocumentsVersion2 from './BusinessV2/DocumentsVersion2';
import isDocumentFirst from './BusinessV2/documentFirst';
import { WIZARD, V2_TYPE } from './BusinessV2/helperV2';
import UserDetailsSectionedV2 from './components/IndividualLite/UserDetailsSectionedV2';
import DocumentFirstChrome from './BusinessV2/components/DocumentFirstChrome';
import { VARIANT } from '../../../util/variant';
import "./styles.scss";

// Wraps the document-first flow in the shared Business V2 appbar (applicant name + draft + save
// state), matching the Business Plus document-first header. Non-doc-first renders children as-is.
const MaybeDocFirstChrome = ({ active, children, ...chromeProps }) =>
    active ? <DocumentFirstChrome {...chromeProps}>{children}</DocumentFirstChrome> : children;

const ONBOARDING_STEP = {
    DOCUMENTS: 0,
    USER_DETAILS: 1,
    DECLARATION_FORM: 2,
    EXPECTED_ACCOUNT_ACTIVITY: 3,
    DOCUMENT_VERIFICATION: 4,
    PAY_NOW: 5,
    KYC_VERIFICATION: 6,
    APPLICATION_FORM_REDO: 7,
    THANK_YOU: Number.MAX_SAFE_INTEGER
}

const INDIVIDUAL_PLUS_TEST_ID = "individual-plus";

// Legacy screens still use local numeric step ids, while autosave gating now lives in JSON
// via semantic state names. This bridge keeps the page wiring thin without pushing repo-
// specific step numbers into shared autosave config.
const AUTO_SAVE_STATE_BY_STEP = {
    [ONBOARDING_STEP.USER_DETAILS]: 'USER_DETAILS',
    [ONBOARDING_STEP.DECLARATION_FORM]: 'DECLARATION_FORM',
    [ONBOARDING_STEP.EXPECTED_ACCOUNT_ACTIVITY]: 'EXPECTED_ACCOUNT_ACTIVITY',
    [ONBOARDING_STEP.APPLICATION_FORM_REDO]: 'EXTRA_INFORMATION',
};

const REVIEW_WIZARD_TO_ONBOARDING_STEP = {
    USER_DETAILS: ONBOARDING_STEP.USER_DETAILS,
    DECLARATION_FORM: ONBOARDING_STEP.DECLARATION_FORM,
    EXPECTED_ACCOUNT_ACTIVITY: ONBOARDING_STEP.EXPECTED_ACCOUNT_ACTIVITY,
    DOCUMENT_VERIFICATION: ONBOARDING_STEP.DOCUMENT_VERIFICATION,
    KYC_VERIFICATION: ONBOARDING_STEP.KYC_VERIFICATION,
};

const getOnboardingStepNumberWhenRedo = (step) => {
    switch (step) {
        case STEPS.APPLICATION_FORM:
            return ONBOARDING_STEP.APPLICATION_FORM_REDO;
        case STEPS.KYC_VERIFICATION:
            return ONBOARDING_STEP.THANK_YOU;
        case STEPS.SANCTIONS_SCREENING:
            return ONBOARDING_STEP.THANK_YOU;
        case STEPS.DOCUMENT_VERIFICATION:
            return ONBOARDING_STEP.THANK_YOU;
        case STEPS.RISK_ASSESSMENT:
            return ONBOARDING_STEP.THANK_YOU;
        default:
            return 0;
    }
}

const ApplyIndividualLite = ({ asLabel, formId, editMode = false, refreshId, brokerMode = false }) => {

    let { individual_id } = useParams();
    const location = useLocation();
    // Broker-managed onboarding (mirrors ApplyBusinessV2:357-359). Gates every broker-only
    // behaviour below; when false the direct self-service flow is unchanged.
    const isBrokerMode = brokerMode === true
        || location.pathname.startsWith('/brokers/applications/')
        || location.pathname.startsWith('/platform/applications/');
    let { showError, showMessage } = useContext(DialogContext);
    const { t } = useTranslation();
    const [data, setData] = useState({});
    const [applicationForm, setApplicationForm] = useState({});
    const [ondatoStatus, setOndatoStatus] = useState({});
    const [isAskForInfo, setIsAskForInfo] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [editId, setEditID] = useState(individual_id ?? formId);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [step, setStep] = useState(null);

    const [showQRCode, setShowQRCode] = useState(false);
    const [qrCodeURL, setQRCodeURL] = useState('');
    const [applicationSteps, setApplicationSteps] = useState({});
    const [polling, setPolling] = useState(null);
    const [cyclosFields, setCyclosFields] = useState({});
    const [autoSaveConfig, setAutoSaveConfig] = useState(null);
    // Full served config (wizardContent, documentConfig, lockRules) for the doc-first path.
    const [jsonConfig, setJsonConfig] = useState(null);
    // Whether the required-documents rule has any FINAL-phase documents (the second collection phase).
    // When there are none, the doc-first broker flow skips the Review/Documents step entirely.
    const [hasFinalDocuments, setHasFinalDocuments] = useState(false);
    const [sysConfigForCardMandatoryForIPlus, setSysConfigForCardMandatoryForIPlus] = useState(false);
    const [reviewOverlayWizard, setReviewOverlayWizard] = useState("");

    // Document-first applies only to broker-managed GMA Plus individuals, and only once the
    // served config actually carries the doc-first sections — so it degrades to the legacy
    // flow if the config has not been published to SysConfig yet.
    const isDocFirst = useMemo(
        () => isDocumentFirst({ applicationForm, isBrokerMode })
            && Boolean(jsonConfig?.wizardContent && jsonConfig?.documentConfig),
        [applicationForm, isBrokerMode, jsonConfig],
    );

    // Doc-first broker flow drops the Review/Documents step (and its stepper circle) when there are
    // no FINAL-phase documents to collect, so the step count is 5 instead of 6.
    const docFirstStepCount = (isDocFirst && isBrokerMode && !hasFinalDocuments) ? 5 : 6;
    const showReviewStep = !isDocFirst || hasFinalDocuments;

    // Doc-first field metadata: the INDIVIDUAL_V2_0 config (jsonConfig) carries doc-first-only field
    // defs — the volume tiers and source of funds — that are not in the Individuals_Lite field set.
    // Gap-fill only (Individuals_Lite wins for anything it already defines), so this never changes a
    // field that already renders; it only exposes the V2-only fields to the doc-first steps.
    const docFirstFields = useMemo(() => {
        // Legacy (existing onboarding + older GMA broker, formVersion < 2) must be untouched: use the
        // plain Individuals_Lite field map, exactly as before the doc-first work.
        if (!isDocFirst) return cyclosFields;
        const v2 = Array.isArray(jsonConfig?.fields) ? createAutoSaveFieldDefinitionMap(jsonConfig.fields) : {};
        const merged = { ...v2, ...cyclosFields };
        // The INDIVIDUAL_V2_0 config owns the doc-first presentation: apply its uiClassName (layout)
        // and its options (select/checkbox choices, e.g. sourcesOfWealth) even for fields that
        // otherwise come from Individuals_Lite, so both stay config-driven in IndividualVersion2.json.
        Object.keys(merged).forEach((field) => {
            const v2Field = v2?.[field];
            if (!v2Field || !merged[field]) return;
            const overrides = {};
            if (v2Field.uiClassName && merged[field].uiClassName !== v2Field.uiClassName) overrides.uiClassName = v2Field.uiClassName;
            if (Array.isArray(v2Field.options) && v2Field.options.length) overrides.options = v2Field.options;
            if (Object.keys(overrides).length) merged[field] = { ...merged[field], ...overrides };
        });
        return merged;
    }, [isDocFirst, jsonConfig, cyclosFields]);

    useEffect(() => { if (formId) setEditID(formId) }, [formId]);

    const handleCopyLink = () => {
        clipboardCopy(qrCodeURL);
        showMessage('Link copied to clipboard!');
    };

    const getFields = async (group) => {
        try {
            if (Object.keys(pincodeService?.records)?.length === 0) {
                pincodeService.fetch().catch(console.error);
            }
            await CountrySelectService.fetch();
            const cyclosResponse = await doGET(`/api/cyclos/fields-json?group=${group}`);
            if (cyclosResponse.status === 200) {
                setAutoSaveConfig(cyclosResponse?.data?.autoSave || null);
                const customFieldObject = createAutoSaveFieldDefinitionMap(cyclosResponse.data?.fields);
                setCyclosFields(customFieldObject);
            }
        } catch (error) {
            showError(error);
        }
    };


    // Doc-first config (wizardContent/documentConfig/lockRules) is served under the
    // INDIVIDUAL_V2_0 group (sysConfig.individualV2_0JSON), SEPARATE from the field
    // definitions served under "Individuals_Lite". Its own `fields` also supply the
    // doc-first-only field defs merged in via docFirstFields (see above).
    const getDocFirstConfig = async () => {
        try {
            const response = await doGET(`/api/cyclos/fields-json?group=INDIVIDUAL_V2_0`);
            if (response?.status === 200) {
                setJsonConfig(response?.data || null);
            }
        } catch (error) {
            // Non-fatal: without this config the flow stays on the legacy individual form.
        }
    };

    const getSysConfigForCardMandatoryForIPlus = async () => {
        try {
            const response = await doGET("/api/sys-config/cardMandatoryForIPlus/detail");
            if (response?.status === 200) {
                setSysConfigForCardMandatoryForIPlus(response.data?.cardMandatoryForIPlus ?? false);
            }
        } catch (error) {

        }
    }

    // Does the required-documents rule request any FINAL-phase documents? Re-checked after extraction,
    // since the second collection phase can be discovered from the parsed documents.
    const checkFinalDocuments = async (recordId = editId) => {
        if (!recordId) return;
        try {
            const response = await doGET(`/api/application/document/required/${recordId}`);
            const docs = response?.data?.documents ?? response?.data ?? [];
            setHasFinalDocuments(Array.isArray(docs) && docs.some((doc) => doc?.phase === 'FINAL'));
        } catch (error) {
            // Non-fatal: default to no FINAL documents (skip the extra step).
        }
    };

    useEffect(() => {
        getFields("Individuals_Lite");
        getDocFirstConfig();
        CountrySelectService.fetchCyclosCountryMaster();
        getSysConfigForCardMandatoryForIPlus();
    }, []);

    useEffect(() => { if (editId) checkFinalDocuments(editId); }, [editId]);


    function handleChange(path, value, isCheckbox = false) {
        setData(prevData => {
            let updatedData = _.cloneDeep(prevData);
            if (isCheckbox) {
                if (_.get(updatedData, path)) {
                    _.set(updatedData, path, _.without(_.get(updatedData, path), value));
                } else {
                    let updatedArray = _.get(updatedData, path) || [];
                    updatedArray.push(value);
                    _.set(updatedData, path, updatedArray);
                }
            } else {
                _.set(updatedData, path, value);
            }

            return updatedData;
        });
    }

    function handleChangeViaObject(updateObject) {
        setData(prevData => {
            let updatedData = _.cloneDeep(prevData);
            Object.keys(updateObject).forEach(key => {
                _.set(updatedData, key, updateObject[key]);
            });
            return updatedData;
        });
    }

    const fetchDetail = async ({ stepProp, usernameProp, syncUi = true, includeMeta = false } = {}) => {
        let individualLiteResponse = {}
        let applicationFormResponse = {}
        if (syncUi) {
            setDataLoading(true)
        }
        try {
            if (editId) {
                localStorage.setItem("formId", editId);
                individualLiteResponse = await fetchIndividualLiteData(editId);
                const applicationResponse = await doGET(`/api/application-form/detail?id=${editId}&withExtension=true`)
                if (applicationResponse?.status == 200) {
                    applicationFormResponse = applicationResponse?.data
                    if (syncUi && applicationFormResponse?.steps?.length) {

                        const askInfoStep = checkIsAskForInfo(applicationFormResponse?.steps)
                        const stepsObj = convertArrayToDict(applicationFormResponse?.steps)

                        setApplicationSteps(stepsObj);

                        if (stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status > 0
                            && [1, 3, 4]?.includes(stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status)
                            && stepsObj[STEPS.APPLICATION_FORM]?.status > 0
                            && [1, 3, 4]?.includes(stepsObj[STEPS.APPLICATION_FORM]?.status)
                        ) {
                            setStep(ONBOARDING_STEP.THANK_YOU);
                        }

                        const applicationFormStepStatus = stepsObj[STEPS.APPLICATION_FORM]?.status;
                        const documentVerificationStepStatus = stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status;
                        const isReadyForKycStage = [applicationFormStepStatus, documentVerificationStepStatus]
                            .every((status) => status > STEP_STATUS.DRAFT && status !== STEP_STATUS.INCOMPLETE);

                        if (!isBrokerMode
                            && isReadyForKycStage
                            && (stepsObj[STEPS.KYC_VERIFICATION]?.status == STEP_STATUS.DRAFT)) {
                            setStep(ONBOARDING_STEP.KYC_VERIFICATION);
                        }

                        if (stepsObj[STEPS.APPLICATION_FORM]?.status > 0 && Object.keys(askInfoStep ?? {}).length) {
                            const askInfoStepData = { ...(askInfoStep ?? {}), isAskForInfo: true, step: getOnboardingStepNumberWhenRedo(askInfoStep?.label), }
                            setIsAskForInfo(askInfoStepData)
                            setStep(ONBOARDING_STEP.THANK_YOU)
                        } else {
                            setIsAskForInfo(null)
                        }
                    }

                    // hide/show pincode field based on country (self-heals a missing pinCode
                    // when the country has a fixed default)
                    individualLiteResponse.address = withComputedHidePinCode(individualLiteResponse.address);

                    const formData = {
                        ...individualLiteResponse,
                        cyclosUserName: usernameProp ? usernameProp : applicationResponse?.data?.cyclosUserName
                    };
                    setApplicationForm(applicationResponse?.data)
                    if (syncUi) {
                        setData(formData)
                    }
                    if (includeMeta) {
                        return {
                            formData,
                            applicationFormData: applicationResponse?.data,
                        };
                    }
                    return formData;
                }
            }
        } catch (error) {
            // showError(error)
        } finally {
            if (syncUi) {
                try {
                    if (editId
                        && (applicationFormResponse?.steps ?? [])?.find(v => v?.label == STEPS.APPLICATION_FORM)?.status != 0) {
                        const ondatoResponse = await doGET(`/api/ondato/form/status?id=${editId}&refresh=true`)
                        if (ondatoResponse?.data) {
                            setOndatoStatus(ondatoResponse?.data)
                        }
                    }
                } catch (error) {
                } finally {
                    setDataLoading(false)
                    if (stepProp) setStep(stepProp)
                    scrollToTop()
                }
            }
        }
        return null;
    }

    const currentStep = step ?? (isDocFirst ? ONBOARDING_STEP.DOCUMENTS : ONBOARDING_STEP.USER_DETAILS);
    const currentAutoSaveState = AUTO_SAVE_STATE_BY_STEP[currentStep] || null;
    const {
        reviewError,
        reviewLoading,
        reviewNavigation,
    } = useFormReviewShell({
        applicationId: editId,
        autoLoad: !asLabel && !editMode && Boolean(editId),
        fieldDefinitions: docFirstFields,
        formData: data,
        currentWizard: reviewOverlayWizard
            || (currentStep === ONBOARDING_STEP.APPLICATION_FORM_REDO ? "ExtraQuestions" : "")
            || currentAutoSaveState
            || "",
        onNavigateToWizard: (wizard) => {
            if (wizard === "ApplicationDocRedo") {
                setReviewOverlayWizard("ApplicationDocRedo");
                scrollToTop();
                return;
            }

            setReviewOverlayWizard("");

            if (wizard === "ExtraQuestions") {
                setStep(ONBOARDING_STEP.APPLICATION_FORM_REDO);
                scrollToTop();
                return;
            }

            const nextStep = REVIEW_WIZARD_TO_ONBOARDING_STEP[wizard];
            if (nextStep) {
                setStep(nextStep);
                scrollToTop();
            }
        },
        onPersistError: showError,
    });

    useEffect(() => {
        if (!reviewNavigation?.hasActionableTasks) {
            return;
        }

        if (
            currentStep === ONBOARDING_STEP.THANK_YOU
            || currentStep > ONBOARDING_STEP.APPLICATION_FORM_REDO
        ) {
            setStep(hasPendingAsks(data?.asks) ? ONBOARDING_STEP.APPLICATION_FORM_REDO : ONBOARDING_STEP.USER_DETAILS);
        }
    }, [currentStep, data?.asks, reviewNavigation?.hasActionableTasks]);

    const isAutoSaveEnabled = !asLabel
        && !!editId
        && !!data?._id
        && !loading
        && !dataLoading
        && isAutoSaveEnabledForConfig({
            autoSaveConfig,
            step: currentAutoSaveState,
        });
    const autoSaveDebounceMs = resolveAutoSaveDebounceMs(autoSaveConfig);

    const handleDirtyUpdate = useCallback(async () => {
        const result = await fetchDetail({ syncUi: false, includeMeta: true });
        if (!result?.formData) {
            return null;
        }

        return {
            data: result.formData,
            relatedRecords: result?.applicationFormData ? [{
                recordKey: 'applicationForm',
                data: result.applicationFormData,
            }] : [],
        };
    }, [fetchDetail]);

    const autoSaveAdapter = useMemo(
        () => createIndividualLiteAutoSaveAdapter(applicationForm, docFirstFields, autoSaveConfig),
        [applicationForm, autoSaveConfig, docFirstFields]
    );

    const syncAutoSaveVersion = useCallback((nextVersion, savedId, recordKey = 'primary') => {
        if (nextVersion === null || nextVersion === undefined || !savedId) return;

        if (recordKey === 'applicationForm') {
            setApplicationForm((prev) => {
                if (!prev?._id || prev._id !== savedId) {
                    return prev;
                }

                return {
                    ...prev,
                    version: nextVersion,
                    cyclosUserName: data?.cyclosUserName ?? prev?.cyclosUserName,
                };
            });
        }
    }, [data?.cyclosUserName]);

    const {
        autoSaveStatus,
        lastSavedAt,
        pendingChangesCount,
        pendingChanges,
        hasUnsavedChanges,
        isAutoSaveNavigationPending,
        flushNow,
        flushAndPauseAutoSave,
        resumeAutoSave,
        syncAfterRefetch,
    } = useAutoSave({
        data,
        isEnabled: isAutoSaveEnabled,
        debounceMs: autoSaveDebounceMs,
        adapter: autoSaveAdapter,
        onDirtyUpdate: handleDirtyUpdate,
        onDataReconcile: setData,
        onFieldPersisted: reviewNavigation?.commitFieldTask,
        onVersionIncrement: syncAutoSaveVersion,
    });

    useUnsavedChangesWarning({
        when: hasUnsavedChanges || loading,
        isSaving: autoSaveStatus === 'saving' || autoSaveStatus === 'resyncing',
        pendingChangesCount,
    });

    const navigate = useNavigate();
    // Mirrors ApplyBusinessV2.handleSaveAndExit: flush the autosave, then return to the broker's
    // applications list. Doc-first is broker-only, so this is the GMA exit path.
    const brokerApplicationsPath = useMemo(() => {
        const basePathMatch = String(location.pathname || '').match(/^(.*)\/applications\//);
        return `${basePathMatch?.[1] || '/broker'}/applications`;
    }, [location.pathname]);
    const handleSaveAndExit = useCallback(async () => {
        try {
            await flushNow?.();
        } catch (e) {
            // best-effort flush before leaving
        }
        navigate(brokerApplicationsPath);
    }, [brokerApplicationsPath, flushNow, navigate]);

    // Document-first chip steps (mirror the Business Plus chrome). Same panes the circle stepper
    // renders and in the same order, so chips + percent stay in sync; non-form panes (Extra Info)
    // fall through with no active chip. Doc-first only — legacy/non-doc-first keep the circle stepper.
    const docFirstChromeStepDefs = [
        isDocFirst && { key: 'documents', label: t('Documents'), num: ONBOARDING_STEP.DOCUMENTS },
        { key: 'user-details', label: t('About You'), num: ONBOARDING_STEP.USER_DETAILS },
        { key: 'declarations', label: t('Declarations'), num: ONBOARDING_STEP.DECLARATION_FORM },
        { key: 'expected-account-activity', label: t('Activity'), num: ONBOARDING_STEP.EXPECTED_ACCOUNT_ACTIVITY },
        (!asLabel && showReviewStep) && { key: 'proof-of-address', label: isDocFirst ? t('Review') : t('Proof Of Address'), num: ONBOARDING_STEP.DOCUMENT_VERIFICATION },
        (!asLabel && !isBrokerMode) && { key: 'pay-now', label: t('Pay Now'), num: ONBOARDING_STEP.PAY_NOW },
        (!asLabel) && { key: 'kyc-verification', label: t('KYC'), num: ONBOARDING_STEP.KYC_VERIFICATION },
    ].filter(Boolean);
    const docFirstActiveStep = docFirstChromeStepDefs.findIndex((s) => s.num === currentStep) + 1;

    const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
    useEffect(() => {
        hasUnsavedChangesRef.current = hasUnsavedChanges;
    }, [hasUnsavedChanges]);

    // When extraction finishes, pull the extracted record into `data` and re-baseline the autosave
    // (syncAfterRefetch), mirroring BusinessV2's adoptServerExtraction. Without this, local `data` keeps
    // the pre-extraction values and the next autosave pushes them back, wiping what extraction wrote.
    const adoptedExtractionRunRef = useRef(null);
    const adoptServerExtraction = useCallback(async ({ runKey } = {}) => {
        if (runKey && adoptedExtractionRunRef.current === runKey) return;
        if (hasUnsavedChangesRef.current) {
            try {
                await flushNow();
            } catch (e) {
                // best-effort flush; still adopt the server's extracted data below
            }
        }
        const fresh = await fetchDetail({ syncUi: false });
        if (!fresh) return;
        if (runKey) adoptedExtractionRunRef.current = runKey;
        setData(fresh);
        syncAfterRefetch(fresh);
        // Parsing can surface FINAL-phase documents; re-check so the Review step appears if needed.
        checkFinalDocuments();
    }, [fetchDetail, flushNow, syncAfterRefetch]);

    // Polling while extraction runs only needs the application/step status refreshed (to enable
    // Continue); it must NOT touch `data`, or an in-flight poll would clobber the user's edits.
    const refreshApplicationFormOnly = useCallback(async () => {
        await fetchDetail({ syncUi: false });
    }, [fetchDetail]);

    const nextActionLoading = loading || isAutoSaveNavigationPending;
    const currentPageReviewTasks = reviewNavigation?.currentWizardActionableTasks || [];

    const checkOndatoStatus = async () => {
        try {
            const ondatoResponse = await doGET(`/api/ondato/form/status?id=${editId}&refresh=true`);
            setOndatoStatus(ondatoResponse?.data);
            if (ondatoResponse?.data?.status === "Completed") {
                fetchDetail({ stepProp: ONBOARDING_STEP.THANK_YOU });
                if (polling)
                    clearInterval(polling);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const startPolling = () => {
        const intervalId = setInterval(() => {
            checkOndatoStatus();
        }, 20000); // Poll every 10 seconds
        setPolling(intervalId);
    };

    useEffect(() => {
        fetchDetail({});
        return () => {
            if (polling) clearInterval(polling);
        };
    }, [editId, refreshId]);

    const handleUpdate = async ({ nextStep, payload, isfetchDetail, isLastStep, skipUpdate }) => {
        let errorFlag = false;
        let latestData = data;
        let didResumeAutoSave = false;
        if ((payload ?? data)?._id) {
            setLoading(true)
            setHasError(false);
            const autoSaveResult = await flushAndPauseAutoSave();
            if (!autoSaveResult?.ok) {
                setLoading(false);
                setHasError(true);
                showError(getAutoSaveProceedBlockedMessage(autoSaveResult?.reason));
                return null;
            }
            latestData = autoSaveResult?.latestData || data;
            const refreshedCurrentPageReviewTasks = reviewNavigation?.peekCurrentWizardActionableTasks?.()
                || currentPageReviewTasks;
            const refreshedActionableReviewTasks = reviewNavigation?.peekActionableTasks?.()
                || reviewNavigation?.actionableTasks
                || [];
            if (nextStep && !isLastStep && !asLabel && !editMode && refreshedCurrentPageReviewTasks.length) {
                const blockedCurrentPageTask = reviewNavigation?.peekPreferredActionableTask?.(reviewNavigation?.currentWizard || "")
                    || refreshedCurrentPageReviewTasks[0];
                resumeAutoSave(latestData);
                didResumeAutoSave = true;
                setLoading(false);
                showError("Please finish the remaining items on this page before continuing.");
                if (blockedCurrentPageTask) {
                    reviewNavigation?.goToTask(blockedCurrentPageTask);
                }
                return { status: 400 };
            }
            if (isLastStep && !asLabel && !editMode && refreshedActionableReviewTasks.length) {
                const blockedActionableTask = reviewNavigation?.peekPreferredActionableTask?.()
                    || refreshedActionableReviewTasks[0];
                resumeAutoSave(latestData);
                didResumeAutoSave = true;
                setLoading(false);
                showError("Please complete all requested items before submitting.");
                if (blockedActionableTask) {
                    reviewNavigation?.goToTask(blockedActionableTask);
                }
                return { status: 400 };
            }
            const updatedPayload = buildPostAutoSaveUpdatePayload(latestData, payload);
            let submissionData = latestData;

            if ('middleName' in updatedPayload && updatedPayload.middleName === "") {
                delete updatedPayload.middleName;
            }

            try {
                if (!asLabel && !skipUpdate) {
                    const response = await doPUT(`/api/individual-lite/update`, updatedPayload)
                    if (response?.status == 200) {
                        const iPlusResponse = response?.data ?? {};

                        // hide/show pincode field based on country (self-heals a missing pinCode)
                        iPlusResponse.address = withComputedHidePinCode(iPlusResponse.address);

                        if (nextStep === 2) {
                            const applicationFormData = await doGET(`/api/application-form/detail?id=${editId}`)
                            if (applicationFormData?.status == 200) {
                                const updatedpplicationFormData = await doPUT(`/api/application-form/update`, {
                                    ...applicationFormData?.data,
                                    cyclosUserName: latestData?.cyclosUserName
                                })
                                if (updatedpplicationFormData?.status == 200) {
                                    const mergedData = {
                                        ...iPlusResponse,
                                        cyclosUserName: updatedpplicationFormData?.data?.cyclosUserName
                                    };
                                    setData(mergedData);
                                    submissionData = mergedData;
                                    resumeAutoSave(mergedData);
                                    didResumeAutoSave = true;
                                }
                                else {
                                    const mergedData = {
                                        ...iPlusResponse,
                                        cyclosUserName: latestData?.cyclosUserName
                                    };
                                    setData(mergedData);
                                    submissionData = mergedData;
                                    resumeAutoSave(mergedData);
                                    didResumeAutoSave = true;
                                }
                            }
                            else {
                                const mergedData = {
                                    ...iPlusResponse,
                                    cyclosUserName: latestData?.cyclosUserName
                                };
                                setData(mergedData);
                                submissionData = mergedData;
                                resumeAutoSave(mergedData);
                                didResumeAutoSave = true;
                            }
                        } else {
                            const mergedData = {
                                ...iPlusResponse,
                                cyclosUserName: latestData?.cyclosUserName
                            };
                            setData(mergedData);
                            submissionData = mergedData;
                            resumeAutoSave(mergedData);
                            didResumeAutoSave = true;
                        }
                    } else {
                        errorFlag = true
                        resumeAutoSave(null);
                        didResumeAutoSave = true;
                    }
                }

                if (isLastStep && !asLabel) {
                    const documentValidation = await validateSubmitDocuments({
                        flow: SUBMIT_DOC_FLOW.INDIVIDUAL_PLUS_ONBOARDING,
                        recordId: editId,
                        data: submissionData,
                        applicationForm,
                        formType: applicationForm?.formType,
                    });

                    if (!documentValidation?.isValid) {
                        setHasError(true);
                        showError(documentValidation?.errors?.[0]?.error || "Please upload all required documents before submitting.");
                        errorFlag = true;
                        return { status: 400 };
                    }

                    const submitResponse = await doGET(`/api/individual-lite/submit?id=${editId}&markFormSubmit=true&markDocSubmit=true`);
                    handleUtmFormSubmit({
                        formType: "INDIVIDUAL_PLUS", utmPayload: {
                            utm_source: applicationForm?.extension?.utm_source,
                            utm_medium: applicationForm?.extension?.utm_medium,
                            utm_campaign: applicationForm?.extension?.utm_campaign,
                            utm_content: applicationForm?.extension?.utm_content,
                            utm_term: applicationForm?.extension?.utm_term,
                        }
                    })
                    if (submitResponse?.status == 200) {
                        await reviewNavigation?.markVisibleTasksResponded?.({ quiet: true });
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        if (isfetchDetail) {
                            await fetchDetail(nextStep ? { stepProp: nextStep } : {});
                            setLoading(false);
                        }
                    } else {
                        errorFlag = true
                    }
                } else {
                    if (nextStep) setStep(nextStep)
                    scrollToTop()
                    setLoading(false);
                }
            } catch (error) {
                setHasError(true);
                showError(error)
                errorFlag = true
                resumeAutoSave(null);
                didResumeAutoSave = true;
            }
            finally {
                if (skipUpdate && !didResumeAutoSave) {
                    resumeAutoSave(null);
                }
                setLoading(false)
            }
        } else {
            if (nextStep) fetchDetail({ stepProp: nextStep })
            resumeAutoSave(null);
        }

        return errorFlag ? { status: 400 } : { status: 200 }
    };

    const requestLink = async () => {
        setLoading(true)
        try {
            const userAgent = navigator.userAgent.toLowerCase();
            const isMobile = /android|iphone/.test(userAgent);
            const response = await doGET(`/api/kyc/qr/url?form_id=${editId}&ubo_id=${data?.ubo_id}`)
            if (isMobile) {
                window.location.href = response.data;
            } else {
                // Show QR code for desktop users
                setShowQRCode(true);
                setQRCodeURL(response.data);
                startPolling(); // Start
            }
        } catch (error) {
            showError(t(error));
        } finally {
            setLoading(false);
        }
    };

    const hasActionableReviewTasks = Boolean(reviewNavigation?.hasActionableTasks);
    const hasVisibleReviewTasks = Boolean(reviewNavigation?.hasVisibleTasks);
    const hasResolvedNoActionableReviewTasks = !reviewLoading && !hasActionableReviewTasks;
    const applicationFormStepStatus = applicationSteps[STEPS.APPLICATION_FORM]?.status;
    const documentVerificationStepStatus = applicationSteps[STEPS.DOCUMENT_VERIFICATION]?.status;
    const kycVerificationStepStatus = applicationSteps[STEPS.KYC_VERIFICATION]?.status;
    const isReadyForKycStage = [applicationFormStepStatus, documentVerificationStepStatus]
        .every((status) => status > STEP_STATUS.DRAFT && status !== STEP_STATUS.INCOMPLETE);
    const isDocumentRedoRequired = applicationFormStepStatus > STEP_STATUS.DRAFT
        && [STEP_STATUS.DRAFT, STEP_STATUS.INCOMPLETE].includes(documentVerificationStepStatus);
    const hasPendingKycStep = !asLabel
        && !isBrokerMode
        && isReadyForKycStage
        && kycVerificationStepStatus === STEP_STATUS.DRAFT
        && ondatoStatus?.status !== "Completed";

    useEffect(() => {
        if (!hasPendingKycStep || currentStep === ONBOARDING_STEP.KYC_VERIFICATION) {
            return;
        }

        setStep(ONBOARDING_STEP.KYC_VERIFICATION);
    }, [currentStep, hasPendingKycStep]);

    const shouldShowReviewWizard = !asLabel
        && !editMode
        && hasVisibleReviewTasks
        && ![
            ONBOARDING_STEP.APPLICATION_FORM_REDO,
            ONBOARDING_STEP.KYC_VERIFICATION,
            ONBOARDING_STEP.THANK_YOU,
        ].includes(currentStep);

    const isDocumentRedoView = !asLabel
        && !hasPendingKycStep
        && (
            reviewOverlayWizard === "ApplicationDocRedo"
            || isDocumentRedoRequired
        );

    const isAskMoreMessageView = !asLabel
        && !hasPendingKycStep
        && hasResolvedNoActionableReviewTasks
        && currentStep > (hasPendingAsks(data?.asks) ? ONBOARDING_STEP.APPLICATION_FORM_REDO : ONBOARDING_STEP.KYC_VERIFICATION)
        && isAskForInfo?.status == STEP_STATUS.INCOMPLETE
        && isAskForInfo?.label == STEPS.APPLICATION_FORM;

    const isExtraQuestionsView = !asLabel
        && !hasPendingKycStep
        && hasPendingAsks(data?.asks)
        && isAskForInfo?.status == STEP_STATUS.INCOMPLETE
        && applicationFormStepStatus == STEP_STATUS.INCOMPLETE
        && isAskForInfo?.label == STEPS.APPLICATION_FORM;

    const isThankYouView = !asLabel
        && !hasPendingKycStep
        && (
            (
                hasResolvedNoActionableReviewTasks
                && currentStep > (hasPendingAsks(data?.asks) ? ONBOARDING_STEP.APPLICATION_FORM_REDO : ONBOARDING_STEP.KYC_VERIFICATION)
            )
            || applicationFormStepStatus == STEP_STATUS.SUBMITTED
        );

    const isLoading = dataLoading || !Object.keys(cyclosFields)?.length || !CountrySelectService?.cyclosCountryMaster?.length;

    const renderApplicantHeaderActions = () => (
        <ApplicantHeaderActions
            applicationForm={applicationForm}
            formData={data}
            formId={editId}
            formType={applicationForm?.formType || FORM_TYPE.IndividualPlus}
            onApplicationFormUpdated={createApplicationFormUpdateHandler(setApplicationForm)}
            disabled={asLabel || editMode}
            logoutComponent={isBrokerMode ? null : <LogoutWithReason form_id={data?._id} testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "logout")} />}
        />
    );

    const renderInFormLayout = (content, showReviewWizard = false) => (
        <div
            className={!asLabel && !editMode ? 'form-content-container xl' : ""}
            data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "shell")}
            data-record-id={editId}
        >
            <div className='form-content-header' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "header")}>
                {!asLabel && <img
                    src={Logo}
                    alt={t("fv-bank")}
                    className="logo logo-dark form-content-logo"
                    data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "logo")}
                />}

                <div className='d-flex align-items-center gap-3' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "header-actions")}>
                    {!asLabel && <div className='' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "language-picker-wrap")}><LanguagePicker testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "language-picker")} /></div>}
                    {renderApplicantHeaderActions()}
                </div>
            </div>

            {isLoading ? <div className=" form-content-body  d-flex justify-content-center align-items-center flex-1" data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "loading")}>
                <FaSpinner size={20} className="spinner" />
            </div> : <Form className={asLabel ? "" : 'form-content-body '} noValidate data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "form")} {...FORM_AUTOFILL_PROPS}>
                {!asLabel && <div className='form-content-body-header' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "body-header")}>
                    <div className="heading" data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "title")}>
                        {t("Individual Plus")}
                        {!isDocFirst && <span data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "autosave")}>
                            <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={lastSavedAt} pendingChangesCount={pendingChangesCount} pendingChanges={pendingChanges} />
                        </span>}
                    </div>
                </div>}
                <div className='form-content-body-content' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "content")}>
                    <div data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "review-shell")}>
                    <ReviewShellBoundary
                        reviewNavigation={reviewNavigation}
                        loading={reviewLoading}
                        error={reviewError}
                        showReviewWizard={showReviewWizard}
                        testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "review-shell-content")}
                    >
                        {content}
                    </ReviewShellBoundary>
                    </div>
                </div>
            </Form>}
        </div>
    );

    if (isDocumentRedoView) {
        if (polling) clearInterval(polling);
        return renderInFormLayout(
            <ApplicationDocRedo
                hideContainerStyle
                recordId={editId}
                asLabel={asLabel}
                formId={editId}
                formType={"INDIVIDUAL_PLUS"}
                type={"Individual Plus"}
            />,
            false
        );
    }

    if (isAskMoreMessageView) {
        return renderInFormLayout(
            <AskMoreMessage
                testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "ask-more-message")}
                hideContainerStyle
                data={data}
                title="Individual Plus"
                remark={(data?.remark ?? "")}
                loading={nextActionLoading}
                onNext={() => { setStep(ONBOARDING_STEP.APPLICATION_FORM_REDO) }}
            />,
            false
        );
    }

    if (isExtraQuestionsView) {
        return renderInFormLayout(
            <ExtraQuestions
                testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "extra-questions")}
                questionIds={data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? []}
                data={data}
                formType={"INDIVIDUAL_PLUS"}
                formId={editId}
                asLabel={asLabel}
                dataLoading={nextActionLoading || dataLoading}
                onSubmit={() => {
                    fetchDetail({
                        stepProp: applicationSteps?.[STEPS.KYC_VERIFICATION]?.status == 0
                            ? ONBOARDING_STEP.KYC_VERIFICATION
                            : ONBOARDING_STEP.THANK_YOU
                    })
                }}
            />,
            false
        );
    }

    if (isThankYouView) {
        if (polling) clearInterval(polling);
        return <ThankYou
            application_id={editId}
            type={FORM_TYPE.IndividualPlus}
            applicationForm={applicationForm}
            formData={data}
            testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "thank-you")}
        />;
    }
    return (
        <div
            className={!asLabel && !editMode ? 'form-content-container xl' : ""}
            data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "shell")}
            data-record-id={editId}
        >
            <div className='form-content-header' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "header")}>
                {!asLabel && <img
                    src={Logo}
                    alt={t("fv-bank")}
                    className="logo logo-dark form-content-logo"
                    data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "logo")}
                />}

                <div className='d-flex align-items-center gap-3' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "header-actions")}>
                    {!asLabel && <div className='' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "language-picker-wrap")}><LanguagePicker testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "language-picker")} /></div>}
                    {renderApplicantHeaderActions()}
                </div>
            </div>

            {isLoading ? <div className=" form-content-body  d-flex justify-content-center align-items-center flex-1" data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "loading")}>
                <FaSpinner size={20} className="spinner" />
            </div> : <Form className={asLabel ? "" : 'form-content-body '} noValidate data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "form")} {...FORM_AUTOFILL_PROPS}>


                {!asLabel && <div className='form-content-body-header' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "body-header")}>
                    <div className="heading" data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "title")}>
                        {t("Individual Plus")}
                        {!isDocFirst && <span data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "autosave")}>
                            <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={lastSavedAt} pendingChangesCount={pendingChangesCount} pendingChanges={pendingChanges} />
                        </span>}
                    </div>
                </div>}
                <div className='form-content-body-content' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "content")}>
                    <div data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "review-shell")}>
                    <ReviewShellBoundary
                        reviewNavigation={reviewNavigation}
                        loading={reviewLoading}
                        error={reviewError}
                        showReviewWizard={shouldShowReviewWizard}
                        testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "review-shell-content")}
                    >
                        <MaybeDocFirstChrome
                            active={isDocFirst}
                            clientName={[data?.firstName, data?.lastName].filter(Boolean).join(' ').trim() || data?.email || t('Individual Plus')}
                            subtitle={`${t('Individual Plus application')} · ${t('draft')}`}
                            steps={docFirstChromeStepDefs}
                            activeStep={docFirstActiveStep}
                            autoSaveStatus={autoSaveStatus}
                            pendingChangesCount={pendingChangesCount}
                            onSaveAndExit={handleSaveAndExit}
                            variant={VARIANT.V2}
                        >
                        <Stepper
                            variant='dark'
                            hideIndicator={isDocFirst}
                            asLabel={asLabel}
                            activeTab={currentStep}
                            numBased={isDocFirst}
                            testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "stepper")}
                            onChange={(s) => {
                                if (s || s === 0) {
                                    setStep(s);
                                }
                            }}

                        >
                            {isDocFirst && (
                                <div
                                    num={ONBOARDING_STEP.DOCUMENTS}
                                    label={t("Documents")}
                                    testIdKey="documents"
                                    data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "step-documents")}
                                >
                                    <DocumentsVersion2
                                        loading={nextActionLoading}
                                        asLabel={asLabel}
                                        hideButtons={asLabel}
                                        data={data}
                                        applicationForm={applicationForm}
                                        editId={editId}
                                        wizard={WIZARD.Documents}
                                        v2Type={V2_TYPE.PLUS}
                                        singleSubject
                                        jsonConfig={jsonConfig}
                                        pageNum={1}
                                        stepCount={docFirstStepCount}
                                        stepLabel={t("Documents")}
                                        handleUpdate={() => setStep(ONBOARDING_STEP.USER_DETAILS)}
                                        onRefetchApplication={adoptServerExtraction}
                                        onPollApplication={refreshApplicationFormOnly}
                                    />
                                </div>
                            )}
                            <div
                                num={ONBOARDING_STEP.USER_DETAILS}
                                label={t("Applicant Information")}
                                testIdKey="user-details"
                                data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "step-user-details")}
                            >
                                {isDocFirst ? (
                                    <UserDetailsSectionedV2
                                        asLabel={asLabel || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)}
                                        hideButtons={asLabel}
                                        data={data}
                                        handleUpdate={handleUpdate}
                                        hasError={hasError}
                                        setHasError={setHasError}
                                        loading={nextActionLoading}
                                        setLoading={setLoading}
                                        handleChangeViaObject={handleChangeViaObject}
                                        cyclosFields={docFirstFields}
                                        jsonConfig={jsonConfig}
                                        isBrokerMode={isBrokerMode}
                                        onOtpVerify={() => { fetchDetail({ usernameProp: data?.cyclosUserName }) }}
                                        handlePrev={() => setStep(ONBOARDING_STEP.DOCUMENTS)}
                                        pageNum={2}
                                        stepCount={docFirstStepCount}
                                        stepLabel={t("About you")}
                                    />
                                ) : (
                                    <UserDetailsForm
                                        asLabel={asLabel || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)}
                                        hideButtons={asLabel}
                                        data={data}
                                        handleChange={handleChange}
                                        setData={setData}
                                        handleUpdate={handleUpdate}
                                        hasError={hasError}
                                        setHasError={setHasError}
                                        loading={nextActionLoading}
                                        setLoading={setLoading}
                                        handleChangeViaObject={handleChangeViaObject}
                                        cyclosFields={cyclosFields}
                                        isBrokerMode={isBrokerMode}
                                        onOtpVerify={() => { fetchDetail({ usernameProp: data?.cyclosUserName }) }}
                                    />
                                )}
                            </div>

                            <div
                                num={ONBOARDING_STEP.DECLARATION_FORM}
                                label={t("Declarations")}
                                testIdKey="declarations"
                                data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "step-declarations")}
                            >
                                <DeclarationsForm
                                    asLabel={asLabel || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)}
                                    hideButtons={asLabel}
                                    data={data}
                                    handleChange={handleChange}
                                    handleChangeObject={setData}
                                    handleUpdate={handleUpdate}
                                    hasError={hasError}
                                    setHasError={setHasError}
                                    loading={nextActionLoading}
                                    setStep={setStep}
                                    cyclosFields={docFirstFields}
                                    isDocumentFirst={isDocFirst}
                                    sysConfigForCardMandatoryForIPlus={sysConfigForCardMandatoryForIPlus}
                                    backStep={ONBOARDING_STEP.USER_DETAILS}
                                    nextStep={ONBOARDING_STEP.EXPECTED_ACCOUNT_ACTIVITY}
                                    pageNum={3}
                                    stepCount={docFirstStepCount}
                                    stepLabel={t("Declarations")}
                                />
                            </div>

                            <div
                                num={ONBOARDING_STEP.EXPECTED_ACCOUNT_ACTIVITY}
                                label={t("Expected Account Activity")}
                                testIdKey="expected-account-activity"
                                data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "step-expected-account-activity")}
                            >
                                <ExpectedAccountActivityForm
                                    asLabel={asLabel || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)}
                                    hideButtons={asLabel}
                                    data={data}
                                    handleChange={handleChange}
                                    handleChangeObject={setData}
                                    handleUpdate={handleUpdate}
                                    hasError={hasError}
                                    setHasError={setHasError}
                                    loading={nextActionLoading}
                                    setStep={setStep}
                                    cyclosFields={docFirstFields}
                                    jsonConfig={jsonConfig}
                                    isDocumentFirst={isDocFirst}
                                    backStep={ONBOARDING_STEP.DECLARATION_FORM}
                                    nextStep={ONBOARDING_STEP.DOCUMENT_VERIFICATION}
                                    pageNum={4}
                                    stepCount={docFirstStepCount}
                                    stepLabel={t("Expected Account Activity")}
                                    isBrokerSubmitStep={!showReviewStep && isBrokerMode}
                                    brokerSubmitNextStep={ONBOARDING_STEP.THANK_YOU}
                                />
                            </div>

                            {!asLabel && showReviewStep && <div
                                num={ONBOARDING_STEP.DOCUMENT_VERIFICATION}
                                label={isDocFirst ? t("Review") : t("Proof Of Address")}
                                testIdKey="proof-of-address"
                                data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "step-proof-of-address")}
                            >
                                <ProofOfAddress
                                    asLabel={asLabel}
                                    hideButtons={asLabel}
                                    data={data}
                                    setStep={setStep}
                                    isAskForInfo={isAskForInfo?.status == STEP_STATUS.INCOMPLETE}
                                    remark={data?.remark}
                                    recordId={editId}
                                    handleUpdate={handleUpdate}
                                    setHasError={setHasError}
                                    backStep={ONBOARDING_STEP.EXPECTED_ACCOUNT_ACTIVITY}
                                    nextStep={ONBOARDING_STEP.PAY_NOW}
                                    isBrokerSubmitStep={isBrokerMode}
                                    brokerSubmitNextStep={ONBOARDING_STEP.THANK_YOU}
                                    isDocumentFirst={isDocFirst}
                                />
                            </div>}

                            {!asLabel && !isBrokerMode && <div
                                num={ONBOARDING_STEP.PAY_NOW}
                                label={t("Pay Now")}
                                testIdKey="pay-now"
                                data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "step-pay-now")}
                            >
                                <PayNow
                                    testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "payment")}
                                    data={{ ...data, formType: "INDIVIDUAL_PLUS" }}
                                    handleUpdateFormData={async (payload) => {
                                        const response = await handleUpdate({ nextStep: ONBOARDING_STEP.PAY_NOW, payload });
                                        return response;
                                    }}
                                    setHasError={setHasError}
                                    onBackClick={() => setStep(ONBOARDING_STEP.DOCUMENT_VERIFICATION)}
                                    shouldCollectAddress={sysConfigForCardMandatoryForIPlus || data?.cardAvail}
                                    onSubmit={async () => {
                                        await handleUpdate({
                                            // nextStep: ONBOARDING_STEP.KYC_VERIFICATION,
                                            isfetchDetail: true,
                                            isLastStep: true,
                                            skipUpdate: true
                                        });
                                    }}
                                />
                            </div>}

                            {!asLabel && <div
                                num={ONBOARDING_STEP.KYC_VERIFICATION}
                                label={t("KYC VERIFICATION")}
                                testIdKey="kyc-verification"
                                data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "step-kyc-verification")}
                            >
                                {showQRCode && !asLabel ? (
                                    <Row data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-qr-state")}>
                                        <StaticKYCContent testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-static-content")} />
                                        <Col className='col-sm-6' style={{ textAlign: 'center', marginBottom: '20px', marginTop: '20px' }} data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-qr-panel")}>
                                            {ondatoStatus?.status == "Completed" ? null : <>
                                                <p data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-qr-instructions")}>{t("Scan this QR code with your mobile device to proceed:")}</p>
                                                <div data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-qr-code")}>
                                                    <QRCode value={qrCodeURL} size={256} level={"H"} includeMargin={true} />
                                                </div>
                                                <div style={{ marginTop: '20px' }} data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-browser-actions")}>
                                                    <p>{t("Can't scan the QR Code?")}
                                                        <a href={qrCodeURL} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '5px' }} data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-browser-link")}>{t("Complete KYC on browser")}</a>
                                                        <FaCopy data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-copy-link")} onClick={handleCopyLink} style={{ cursor: 'pointer', marginLeft: '10px' }} />
                                                    </p>

                                                </div>
                                            </>
                                            }

                                            <div className='mt-4 d-flex w-100 align-items-center justify-content-center' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-status")}>
                                                {ondatoStatus?.status &&
                                                    <StatusWithBadge
                                                        testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-status-badge")}
                                                        title="Status"
                                                        status={ondatoStatus?.status ?? "Pending"}
                                                    />
                                                }

                                            </div>

                                        </Col>
                                    </Row>
                                ) : <div data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-pre-info")}>
                                    <PreKYCInfo
                                        testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "kyc-pre-info-content")}
                                        asLabel={asLabel}
                                        isAskForInfo={isAskForInfo?.status == STEP_STATUS.INCOMPLETE}
                                        loading={nextActionLoading}
                                        onNext={() => {
                                            if (applicationForm?.steps?.[2]?.status == 3 || applicationForm?.steps?.[2]?.status == 4 || ondatoStatus?.status == "Completed") {
                                                setStep(ONBOARDING_STEP.THANK_YOU)
                                            } else {
                                                requestLink()
                                            }
                                        }}
                                    />
                                </div>}
                            </div>}

                            {
                                (((hasPendingAsks(data?.asks)
                                    && isAskForInfo?.status == STEP_STATUS.INCOMPLETE
                                    && applicationSteps[STEPS.APPLICATION_FORM]?.status == 2))
                                    || (asLabel && hasActiveAsks(data?.asks))
                                )
                                && <div
                                    num={ONBOARDING_STEP.APPLICATION_FORM_REDO}
                                    label={"Extra Information"}
                                    testIdKey="extra-information"
                                    data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "step-extra-information")}
                                >
                                    <ExtraQuestions
                                        testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_TEST_ID, "extra-questions")}
                                        questionIds={data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? []}
                                        data={data}
                                        formType={"INDIVIDUAL_PLUS"}
                                        formId={editId}
                                        asLabel={asLabel}
                                        dataLoading={nextActionLoading || dataLoading}
                                        onSubmit={() => {
                                            fetchDetail({ stepProp: applicationSteps?.[STEPS.KYC_VERIFICATION]?.status == 0 ? ONBOARDING_STEP.KYC_VERIFICATION : ONBOARDING_STEP.THANK_YOU })
                                        }}
                                    />
                                </div>
                            }
                        </Stepper>
                        </MaybeDocFirstChrome>
                    </ReviewShellBoundary>
                    </div>
                </div>
            </Form>}
        </div>
    );
};

export default observer(ApplyIndividualLite);
